// PWA Utility Functions
// Handles service worker registration, installation prompts, and PWA features

import logger from '../utils/logger';
class PWAManager {
  constructor() {
    this.deferredPrompt = null;
    this.isInstalled = false;
    this.isOnline = navigator.onLine;
    this.registration = null;
    
    this.init();
  }
  
  async init() {
    await this.registerServiceWorker();
    // this.setupInstallPrompt(); // DISABLED: Install prompts removed
    this.setupOnlineOfflineHandlers();
    this.setupUpdateHandlers();
    this.checkInstallationStatus();
  }
  
  // Service Worker Registration
  async registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        this.registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
          updateViaCache: 'none' // Force check for updates every time
        });
        
        logger.info('PWA', 'Info', this.registration);
        
        // Check for updates immediately
        await this.registration.update();
        
        // Handle service worker updates
        this.registration.addEventListener('updatefound', () => {
          const newWorker = this.registration.installing;
          logger.info('PWA', 'Info');
          
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              logger.info('PWA', 'Info');
              this.showUpdateNotification();
            }
          });
        });
        
        // Check for updates every 30 minutes
        setInterval(() => {
          logger.info('PWA', 'Info');
          this.registration.update();
        }, 30 * 60 * 1000); // 30 minutes
        
        // Also check for updates when page becomes visible
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            logger.info('PWA', 'Info');
            this.registration.update();
          }
        });
        
        // Handle controller change (when new SW takes over after SKIP_WAITING)
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          logger.info('PWA', 'Info');
          // Reload the page once when new service worker takes over
          if (!refreshing) {
            refreshing = true;
            logger.info('PWA', 'Info');
            window.location.href = window.location.href;
          }
        });
        
      } catch (error) {
        logger.error('PWA', 'Error', error);
      }
    } else {
      logger.warn('PWA', 'Warning');
    }
  }
  
  // Installation Prompt Setup
  setupInstallPrompt() {
    window.addEventListener('beforeinstallprompt', (e) => {
      logger.info('PWA', 'Info');
      e.preventDefault();
      this.deferredPrompt = e;
      this.showInstallButton();
    });
    
    window.addEventListener('appinstalled', () => {
      logger.info('PWA', 'Info');
      this.isInstalled = true;
      this.hideInstallButton();
      this.deferredPrompt = null;
      
      // Track installation
      if (window.gtag) {
        window.gtag('event', 'pwa_installed', {
          event_category: 'PWA',
          event_label: 'App Installation'
        });
      }
    });
  }
  
  // Online/Offline Handlers
  setupOnlineOfflineHandlers() {
    window.addEventListener('online', () => {
      logger.info('PWA', 'Info');
      this.isOnline = true;
      this.showOnlineStatus();
      this.syncOfflineData();
    });
    
    window.addEventListener('offline', () => {
      logger.info('PWA', 'Info');
      this.isOnline = false;
      this.showOfflineStatus();
    });
  }
  
  // Update Handlers
  setupUpdateHandlers() {
    if (this.registration) {
      this.registration.addEventListener('updatefound', () => {
        const newWorker = this.registration.installing;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            this.showUpdateNotification();
          }
        });
      });
    }
  }
  
  // Check Installation Status
  checkInstallationStatus() {
    // Check if app is running in standalone mode
    this.isInstalled = window.matchMedia('(display-mode: standalone)').matches ||
                      window.navigator.standalone === true;
    
    if (this.isInstalled) {
      this.hideInstallButton();
    }
  }
  
  // Show Install Button
  showInstallButton() {
    const installButton = document.getElementById('pwa-install-button');
    if (installButton) {
      installButton.style.display = 'block';
      installButton.addEventListener('click', () => this.installApp());
    }
  }
  
  // Hide Install Button
  hideInstallButton() {
    const installButton = document.getElementById('pwa-install-button');
    if (installButton) {
      installButton.style.display = 'none';
    }
  }
  
  // Install App
  async installApp() {
    if (!this.deferredPrompt) {
      logger.warn('PWA', 'Warning');
      return;
    }
    
    try {
      this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      
      logger.info('PWA', 'Info', outcome);
      
      if (outcome === 'accepted') {
        logger.info('PWA', 'Info');
      } else {
        logger.info('PWA', 'Info');
      }
      
      this.deferredPrompt = null;
      this.hideInstallButton();
      
    } catch (error) {
      logger.error('PWA', 'Error', error);
    }
  }
  
  // Show Update Notification
  showUpdateNotification() {
    if (this.isUpdateNotificationShown) return;
    
    // Check if we're in a loading loop (multiple update notifications in short time)
    const now = Date.now();
    if (this.lastUpdateNotificationTime && (now - this.lastUpdateNotificationTime) < 5000) {
      logger.info('PWA', 'Info');
      return;
    }
    
    this.isUpdateNotificationShown = true;
    this.lastUpdateNotificationTime = now;
    
    const notification = document.createElement('div');
    notification.id = 'pwa-update-notification';
    notification.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #10B981;
        color: white;
        padding: 16px;
        border-radius: 12px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10000;
        max-width: 300px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <div style="font-weight: 600; margin-bottom: 8px;">App Update Available</div>
        <div style="font-size: 14px; margin-bottom: 12px; opacity: 0.9;">
          A new version of KinsThread is ready. Refresh to get the latest features.
        </div>
        <div style="display: flex; gap: 8px;">
          <button id="pwa-update-btn" style="
            background: white;
            color: #10B981;
            border: none;
            padding: 8px 16px;
            border-radius: 6px;
            font-weight: 600;
            cursor: pointer;
            font-size: 14px;
          ">Update Now</button>
          <button id="pwa-update-dismiss" style="
            background: transparent;
            color: white;
            border: 1px solid rgba(255,255,255,0.3);
            padding: 8px 16px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
          ">Later</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Add event listeners
    document.getElementById('pwa-update-btn').addEventListener('click', () => {
      this.updateApp();
    });
    
    document.getElementById('pwa-update-dismiss').addEventListener('click', () => {
      this.dismissUpdateNotification();
    });
    
    // Auto-dismiss after 10 seconds
    setTimeout(() => {
      this.dismissUpdateNotification();
    }, 10000);
  }
  
  // Update App
  async updateApp() {
    if (this.registration && this.registration.waiting) {
      // Clear all caches to prevent loading old files
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
        logger.info('PWA', 'Info');
      } catch (error) {
        logger.error('PWA', 'Error', error);
      }
      
      // Tell the waiting service worker to activate
      // The controllerchange listener will handle the reload
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }
  
  // Dismiss Update Notification
  dismissUpdateNotification() {
    const notification = document.getElementById('pwa-update-notification');
    if (notification) {
      notification.remove();
      this.isUpdateNotificationShown = false;
    }
  }
  
  // Show Online Status
  showOnlineStatus() {
    this.showStatusMessage('Back online!', 'success');
  }
  
  // Show Offline Status
  showOfflineStatus() {
    this.showStatusMessage('You\'re offline. Some features may be limited.', 'warning');
  }
  
  // Show Status Message
  showStatusMessage(message, type = 'info') {
    const statusEl = document.getElementById('pwa-status');
    if (statusEl) {
      statusEl.textContent = message;
      statusEl.className = `pwa-status pwa-status-${type}`;
      statusEl.style.display = 'block';
      
      setTimeout(() => {
        statusEl.style.display = 'none';
      }, 3000);
    }
  }
  
  // Sync Offline Data
  async syncOfflineData() {
    if ('serviceWorker' in navigator && this.registration) {
      try {
        // Trigger background sync
        await this.registration.sync.register('progress-sync');
        await this.registration.sync.register('analytics-sync');
        logger.info('PWA', 'Info');
      } catch (error) {
        logger.error('PWA', 'Error', error);
      }
    }
  }
  
  // Cache URLs
  async cacheUrls(urls) {
    if ('serviceWorker' in navigator && this.registration) {
      try {
        const message = {
          type: 'CACHE_URLS',
          urls: urls
        };
        
        if (this.registration.active) {
          this.registration.active.postMessage(message);
        }
        
        logger.info('PWA', 'Info', urls);
      } catch (error) {
        logger.error('PWA', 'Error', error);
      }
    }
  }
  
  // Get Installation Status
  getInstallationStatus() {
    return {
      isInstalled: this.isInstalled,
      canInstall: !!this.deferredPrompt,
      isOnline: this.isOnline,
      hasServiceWorker: !!this.registration
    };
  }
  
  // Request Notification Permission
  async requestNotificationPermission() {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      logger.info('PWA', 'Info', permission);
      return permission === 'granted';
    }
    return false;
  }
  
  // Send Test Notification
  async sendTestNotification() {
    if ('serviceWorker' in navigator && this.registration) {
      try {
        await this.registration.showNotification('KinsThread Test', {
          body: 'This is a test notification from KinsThread!',
          icon: '/icons/icon-192x192.png',
          badge: '/icons/badge-72x72.png',
          tag: 'test-notification'
        });
      } catch (error) {
        logger.error('PWA', 'Error', error);
      }
    }
  }
  
  // Manual Update Check
  async checkForUpdates() {
    if (!this.registration) {
      logger.warn('PWA', 'Warning');
      return { updateAvailable: false, message: 'Service worker not registered' };
    }
    
    try {
      logger.info('PWA', 'Info');
      await this.registration.update();
      
      // Wait a bit for the update to be detected
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (this.registration.waiting) {
        logger.info('PWA', 'Info');
        return { updateAvailable: true, message: 'Update is ready to install' };
      } else if (this.registration.installing) {
        logger.info('PWA', 'Info');
        return { updateAvailable: true, message: 'Update is downloading...' };
      } else {
        logger.info('PWA', 'Info');
        return { updateAvailable: false, message: 'You have the latest version' };
      }
    } catch (error) {
      logger.error('PWA', 'Error', error);
      return { updateAvailable: false, message: 'Failed to check for updates' };
    }
  }
  
  // Get App Version
  async getAppVersion() {
    try {
      const response = await fetch('/app-version.json');
      const data = await response.json();
      return data.version;
    } catch (error) {
      logger.error('PWA', 'Error', error);
      return '2.0.0'; // Default version
    }
  }
}

// Create global instance
const pwaManager = new PWAManager();

// Export for use in other modules
export default pwaManager;

// Export individual functions for convenience
export const {
  installApp,
  updateApp,
  cacheUrls,
  getInstallationStatus,
  requestNotificationPermission,
  sendTestNotification,
  checkForUpdates,
  getAppVersion
} = pwaManager;


